
import { useState, useCallback, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Careers from './components/Careers';
import Investors from './components/Investors';
import Footer from './components/Footer';
import ProjectsModal from './components/ProjectsModal';
import LoginModal from './components/LoginModal';
import AdminPanel from './components/AdminPanel';
import Maintenance from './components/Maintenance';
import PartnershipPage from './components/PartnershipPage';
import WhatsAppButton from './components/WhatsAppButton';
import LegalPage from './components/LegalPage';
import ErrorReportModal from './components/ErrorReportModal';
import ClientArea from './components/ClientArea';
import { Project, JobOpening, InvestorLead, PartnershipLead, Language, Client } from './types';
import { translations } from './i18n';

// Cambia esta bandera a 'false' para desactivar el modo de mantenimiento
const MAINTENANCE_MODE = false;

type PageState = 'home' | 'legal' | 'privacy' | 'cookies' | 'terms' | 'client-area';

const App = () => {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const langParam = params.get('lang') as Language;
      const validLanguages: Language[] = ['es', 'en', 'fr', 'ru', 'ua', 'cat'];
      if (langParam && validLanguages.includes(langParam)) {
        return langParam;
      }
      
      const savedLang = localStorage.getItem('negolan_lang') as Language;
      if (savedLang && validLanguages.includes(savedLang)) {
        return savedLang;
      }

      const browserLang = navigator.language.split('-')[0] as Language;
      if (browserLang && validLanguages.includes(browserLang)) {
        return browserLang;
      }
    } catch (e) {
      console.error("Failed to parse language from environment:", e);
    }
    return 'es';
  });

  const [activePage, setActivePage] = useState<PageState>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get('page') as PageState;
      const validPages: PageState[] = ['home', 'legal', 'privacy', 'cookies', 'terms', 'client-area'];
      if (pageParam && validPages.includes(pageParam)) {
        return pageParam;
      }
    } catch (e) {
      console.error("Failed to parse activePage from URL:", e);
    }
    return 'home';
  });

  const [isErrorReportModalOpen, setErrorReportModalOpen] = useState(false);
  const [d1Status, setD1Status] = useState<{ loading: boolean; success?: boolean; error?: string }>({ loading: true });
  const t = translations[language];

  // Dynamic SEO & SEM Updates (Title, Meta, Canonical, Hreflangs, Lang attribute & LocalStorage Sync)
  useEffect(() => {
    if (t.seo) {
      document.title = t.seo.title;
      document.documentElement.lang = language === 'cat' ? 'ca' : language;
      
      // Update meta description (or create if missing)
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', t.seo.description);

      // Update meta keywords (or create if missing)
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', t.seo.keywords);

      // Update OpenGraph
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', t.seo.title);
      
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', t.seo.description);

      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute('content', window.location.href);

      // Update Twitter
      const twTitle = document.querySelector('meta[property="twitter:title"]');
      if (twTitle) twTitle.setAttribute('content', t.seo.title);

      const twDesc = document.querySelector('meta[property="twitter:description"]');
      if (twDesc) twDesc.setAttribute('content', t.seo.description);

      // Save user selected language for next visit persistence
      localStorage.setItem('negolan_lang', language);

      // Update URL search parameters seamlessly to support direct linking (essential for SEM/SEO)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('lang', language);
        if (activePage !== 'home') {
          url.searchParams.set('page', activePage);
        } else {
          url.searchParams.delete('page');
        }
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.error("Failed to update URL search params:", e);
      }

      // Generate dynamic hreflang alternate links & canonical markers for search engine crawlers
      try {
        const validLanguages: Language[] = ['es', 'en', 'fr', 'ru', 'ua', 'cat'];
        
        // Remove existing dynamic tags to prevent piling up
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
        document.querySelectorAll('link[rel="canonical"]').forEach(el => el.remove());
        
        const baseUrl = 'https://negolan.com/';
        
        // Canonical relationship
        const canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        const canonicalPageStr = activePage !== 'home' ? `?page=${activePage}&lang=${language}` : `?lang=${language}`;
        canonicalLink.href = baseUrl + canonicalPageStr;
        document.head.appendChild(canonicalLink);

        // x-default fallback for unspecified region/language matches
        const defaultLink = document.createElement('link');
        defaultLink.rel = 'alternate';
        defaultLink.setAttribute('hreflang', 'x-default');
        defaultLink.href = baseUrl;
        document.head.appendChild(defaultLink);

        // Individual language alternate declarations
        validLanguages.forEach((lang) => {
          const link = document.createElement('link');
          link.rel = 'alternate';
          link.setAttribute('hreflang', lang === 'cat' ? 'ca' : lang);
          const pageQuery = activePage !== 'home' ? `?page=${activePage}&lang=${lang}` : `?lang=${lang}`;
          link.href = baseUrl + pageQuery;
          document.head.appendChild(link);
        });
      } catch (err) {
        console.error("Failed to update hreflang/canonical tags:", err);
      }
    }
  }, [language, activePage, t]);

  // Test D1 connection and fetch data on load
  useEffect(() => {
    const checkConnectivity = async () => {
      try {
        // First, check basic API responsiveness
        const pingRes = await fetch('/api/ping').catch(() => null);
        if (pingRes && pingRes.ok) {
          console.log("Basic API check (ping) successful");
        } else {
          console.warn("Basic API check (ping) failed or not reachable");
        }

        const res = await fetch('/api/d1/test', {
          method: 'GET',
          cache: 'no-store'
        });
        
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await res.json();
          if (data.success) {
            setD1Status({ loading: false, success: true });
          } else {
            setD1Status({ 
              loading: false, 
              success: false, 
              error: data.error ? (typeof data.error === "string" ? data.error : JSON.stringify(data.error)) : "Error de respuesta API" 
            });
          }
        } else {
          // If not JSON, it's likely an HTML error page from the proxy/server
          const text = await res.text();
          const preview = text.slice(0, 100).replace(/</g, "&lt;");
          setD1Status({ 
            loading: false, 
            success: false, 
            error: `Error de Servidor (${res.status}): No es JSON. Inicio de respuesta: ${preview}. Compruebe variables de entorno CLOUDFLARE_* en producción.` 
          });
        }
      } catch (err: any) {
        setD1Status({ loading: false, success: false, error: err.message });
      }
    };

    checkConnectivity();

    // Re-check periodically to ensure status stays current
    const interval = setInterval(checkConnectivity, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch sequentially to avoid rate limiting (429) from CF Management API
        const fetchSafely = async (url: string) => {
          try {
            const res = await fetch(url);
            if (res.ok) return await res.json().catch(() => ({ success: false }));
            return { success: false };
          } catch (e) {
            return { success: false };
          }
        };

        const projDataRaw = await fetchSafely('/api/projects');
        const jobDataRaw = await fetchSafely('/api/jobs');
        const invDataRaw = await fetchSafely('/api/admin/leads/investors');
        const partDataRaw = await fetchSafely('/api/admin/leads/partnerships');
        
        const safeParse = (data: any) => {
          if (typeof data === 'string') {
            try {
              return JSON.parse(data);
            } catch (e) {
              console.warn("Failed to parse JSON string:", data);
              return data;
            }
          }
          return data;
        };

        if (projDataRaw.success && Array.isArray(projDataRaw.projects)) {
          setProjects(projDataRaw.projects.map((p: any) => ({
            ...p,
            city: safeParse(p.city),
            area: safeParse(p.area),
            publicDescription: safeParse(p.publicDescription),
            privateDetails: safeParse(p.privateDetails),
          })));
        }

        if (jobDataRaw.success && Array.isArray(jobDataRaw.jobs)) {
          setJobs(jobDataRaw.jobs.map((j: any) => ({
            ...j,
            title: safeParse(j.title),
            description: safeParse(j.description),
            location: safeParse(j.location),
            type: safeParse(j.type),
          })));
        }

        if (invDataRaw.success && (Array.isArray(invDataRaw.data) || Array.isArray(invDataRaw.leads))) {
           const items = invDataRaw.data || invDataRaw.leads || [];
           setInvestorLeads(items.map((l: any) => ({
             ...l,
             date: new Date(l.created_at)
           })));
        }

        if (partDataRaw.success && (Array.isArray(partDataRaw.data) || Array.isArray(partDataRaw.leads))) {
           const items = partDataRaw.data || partDataRaw.leads || [];
           setPartnershipLeads(items.map((l: any) => ({
             ...l,
             projectName: l.project_name,
             collaborationType: l.collaboration_type,
             companyName: l.company_name,
             contactPerson: l.contact_person,
             companyDescription: l.company_description,
             detailedProposal: l.detailed_proposal,
             estimatedBudget: l.estimated_budget,
             ndaAccepted: l.nda_accepted === 1,
             date: new Date(l.created_at)
           })));
        }
      } catch (err) {
        console.error("Failed to fetch dynamic data:", err);
      }
    };
    fetchData();
  }, []);

  if (MAINTENANCE_MODE) {
    return <Maintenance t={t.maintenance} />;
  }

  const [isProjectsModalOpen, setProjectsModalOpen] = useState(false);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isAdminPanelOpen, setAdminPanelOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedClient, setLoggedClient] = useState<Client | null>(null);
  const isAdmin = isLoggedIn && !loggedClient;
  const [selectedProjectForPartnership, setSelectedProjectForPartnership] = useState<Project | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [investorLeads, setInvestorLeads] = useState<InvestorLead[]>([]);
  const [partnershipLeads, setPartnershipLeads] = useState<PartnershipLead[]>([]);

  // Filter projects if it's a client
  const visibleProjects = loggedClient 
    ? projects.filter(p => loggedClient.project_ids.split(',').map(s => s.trim()).includes(p.id))
    : projects;

  // Auth persistence check
  useEffect(() => {
    const savedLogin = localStorage.getItem('negolan_session');
    if (savedLogin) {
      try {
        const session = JSON.parse(savedLogin);
        if (session.type === 'admin') {
          setIsLoggedIn(true);
          // Auto-open admin panel if they were there? Or just stay logged in.
          // For now, just stay logged in.
        } else if (session.type === 'client' && session.client) {
          setLoggedClient(session.client);
          setIsLoggedIn(true);
        }
      } catch (e) {
        console.error("Error restoring session:", e);
        localStorage.removeItem('negolan_session');
      }
    }
  }, []);

  // Auth persistence check
  useEffect(() => {
    const savedLogin = localStorage.getItem('negolan_session');
    if (savedLogin) {
      try {
        const session = JSON.parse(savedLogin);
        if (session.type === 'admin') {
          setIsLoggedIn(true);
          // Auto-open admin panel if they were there? Or just stay logged in.
          // For now, just stay logged in.
        } else if (session.type === 'client' && session.client) {
          setLoggedClient(session.client);
          setIsLoggedIn(true);
        }
      } catch (e) {
        console.error("Error restoring session:", e);
        localStorage.removeItem('negolan_session');
      }
    }
  }, []);

  const handleOpenProjectsModal = useCallback(() => setProjectsModalOpen(true), []);
  const handleCloseProjectsModal = useCallback(() => setProjectsModalOpen(false), []);
  
  const handleCloseAdminPanel = useCallback(() => setAdminPanelOpen(false), []);

  const handleOpenLoginModal = useCallback(() => {
    setProjectsModalOpen(false); 
    setAdminPanelOpen(false);
    setSelectedProjectForPartnership(null);
    setLoginModalOpen(true);
  }, []);

  const handleCloseLoginModal = useCallback(() => setLoginModalOpen(false), []);

  const handleLogin = useCallback((client?: Client) => {
    if (client) {
      setLoggedClient(client);
      setIsLoggedIn(true);
      setActivePage('client-area');
      localStorage.setItem('negolan_session', JSON.stringify({ type: 'client', client }));
    } else {
      // It's the master admin login
      setIsLoggedIn(true);
      setAdminPanelOpen(true);
      localStorage.setItem('negolan_session', JSON.stringify({ type: 'admin' }));
    }
    setLoginModalOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    setLoggedClient(null);
    setAdminPanelOpen(false);
    setActivePage('home');
    localStorage.removeItem('negolan_session');
  }, []);
  
  const handleOpenPartnershipPage = useCallback((project: Project) => {
    setProjectsModalOpen(false);
    setSelectedProjectForPartnership(project);
  }, []);
  
  const handleClosePartnershipPage = useCallback(() => {
    setSelectedProjectForPartnership(null);
  }, []);

  const handleAddInvestorLead = useCallback(async (leadData: Omit<InvestorLead, 'id' | 'date'>) => {
      try {
        const response = await fetch('/api/admin/leads/investors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadData)
        });
        if (response.ok) {
           // Refresh list
           const res = await fetch('/api/admin/leads/investors');
           const data = await res.json();
           if (data.success) {
             const items = data.data || data.leads || [];
             setInvestorLeads(items.map((l: any) => ({ ...l, date: new Date(l.created_at) })));
           }
        }
      } catch (err) {
        console.error("Error saving investor lead:", err);
      }
  }, []);

  const handleAddPartnershipLead = useCallback(async (leadData: Omit<PartnershipLead, 'id' | 'date'>) => {
    try {
      const dbData = {
        contact_person: leadData.contactPerson,
        company_name: leadData.companyName,
        email: leadData.email,
        phone: leadData.phone,
        website: leadData.website,
        country: leadData.country,
        project_name: leadData.projectName,
        collaboration_type: leadData.collaborationType,
        estimated_budget: leadData.estimatedBudget,
        company_description: leadData.companyDescription,
        detailed_proposal: leadData.detailedProposal,
        nda_accepted: leadData.ndaAccepted
      };

      const response = await fetch('/api/admin/leads/partnerships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dbData)
      });

      if (response.ok) {
         // Refresh list
         const res = await fetch('/api/admin/leads/partnerships');
         const data = await res.json();
         if (data.success) {
           const items = data.data || data.leads || [];
           setPartnershipLeads(items.map((l: any) => ({
             ...l,
             projectName: l.project_name,
             collaborationType: l.collaboration_type,
             companyName: l.company_name,
             contactPerson: l.contact_person,
             companyDescription: l.company_description,
             detailedProposal: l.detailed_proposal,
             estimatedBudget: l.estimated_budget,
             ndaAccepted: l.nda_accepted === 1,
             date: new Date(l.created_at)
           })));
         }
      }
    } catch (err) {
      console.error("Error saving partnership lead:", err);
    }
  }, []);

  const handleNavigate = useCallback((page: PageState) => {
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleOpenErrorReportModal = useCallback(() => setErrorReportModalOpen(true), []);
  const handleCloseErrorReportModal = useCallback(() => setErrorReportModalOpen(false), []);

  return (
    <div className="text-slate-200 min-h-screen flex flex-col">
      {/* Indicador de conexión D1 (oculto en móviles para evitar colisiones) */}
      {!MAINTENANCE_MODE && (
        <div className="hidden md:block fixed bottom-20 left-4 z-[100] scale-90 md:scale-100">
          {d1Status.loading ? (
            <div className="bg-slate-800/90 text-slate-400 px-3 py-1.5 rounded-full border border-slate-700 flex items-center space-x-2 text-xs">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              <span>Verificando conexión con D1...</span>
            </div>
          ) : d1Status.success ? (
            <div className="bg-green-900/40 text-green-400 px-3 py-1.5 rounded-full border border-green-500/30 flex items-center space-x-2 text-xs backdrop-blur-sm group hover:bg-green-900/60 transition-colors">
              <div className="w-2 h-2 bg-green-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
              <span>Conectado a Cloudflare D1</span>
            </div>
          ) : (
            <div className="bg-red-900/40 text-red-400 px-3 py-1.5 rounded-full border border-red-500/30 flex items-center space-x-2 text-xs backdrop-blur-sm" title={d1Status.error}>
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span>D1 Desconectado</span>
            </div>
          )}
        </div>
      )}

      {!isAdminPanelOpen && (
        <Header 
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          isClient={!!loggedClient}
          onNavigate={handleNavigate}
          onLoginClick={handleOpenLoginModal}
          onLogoutClick={handleLogout}
          onProjectsClick={handleOpenProjectsModal}
          language={language}
          setLanguage={setLanguage}
          t={t.header}
          translationWarning={(t as any).translationWarning}
        />
      )}
      
      <div className="flex-grow">
        {isLoggedIn && !loggedClient && !isAdminPanelOpen && (
           <div className="bg-yellow-600/20 border-b border-yellow-500/30 py-2 px-4 text-center text-xs animate-fade-in cursor-pointer hover:bg-yellow-600/30 transition-colors" onClick={() => setAdminPanelOpen(true)}>
             Sesión de Administrador activa. Cliquea aquí para volver al Panel de Control.
           </div>
        )}
        {loggedClient && activePage !== 'client-area' && (
          <div className="bg-blue-600/20 border-b border-blue-500/30 py-2 px-4 text-center text-xs animate-fade-in cursor-pointer hover:bg-blue-600/30 transition-colors" onClick={() => handleNavigate('client-area')}>
            Bienvenido, <span className="font-bold text-blue-400">{loggedClient.name}</span>. Tienes acceso exclusivo a {loggedClient.project_ids.split(',').length} proyectos. Cliquea aquí para ir a tu Área de Cliente.
          </div>
        )}
        {isAdminPanelOpen && isLoggedIn ? (
          <AdminPanel 
             onClose={handleCloseAdminPanel}
             onLogout={handleLogout}
             logoutText={t.header.logout}
             projects={visibleProjects}
             setProjects={setProjects}
             jobs={jobs}
             setJobs={setJobs}
             investorLeads={investorLeads}
             setInvestorLeads={setInvestorLeads}
             partnershipLeads={partnershipLeads}
             setPartnershipLeads={setPartnershipLeads}
             language={language}
             t={t.adminPanel}
          />
        ) : activePage === 'client-area' && loggedClient ? (
          <ClientArea 
            client={loggedClient} 
            projects={visibleProjects} 
            language={language}
            onLogout={handleLogout}
            onProjectClick={(p: Project) => {
              setProjectsModalOpen(true);
            }}
            t={t}
          />
        ) : activePage !== 'home' ? (
          <LegalPage page={activePage as any} onNavigate={handleNavigate} language={language} />
        ) : (
          <main>
            <Hero onProjectsClick={handleOpenProjectsModal} t={t.hero} />
            <About t={t.about} />
            <Careers jobs={jobs} language={language} t={t.careers} />
            <Investors onAddLead={handleAddInvestorLead} t={t.investors} />
          </main>
        )}
      </div>

      <Footer onNavigate={handleNavigate} onErrorReport={handleOpenErrorReportModal} t={t.footer}/>

      <ProjectsModal 
        isOpen={isProjectsModalOpen}
        onClose={handleCloseProjectsModal}
        isLoggedIn={isLoggedIn}
        onLoginRequest={handleOpenLoginModal}
        projects={visibleProjects}
        onPartnershipRequest={handleOpenPartnershipPage}
        language={language}
        t={t.projectsModal}
      />

      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={handleCloseLoginModal}
        onLogin={handleLogin}
        t={t.loginModal}
      />

      <ErrorReportModal 
        isOpen={isErrorReportModalOpen}
        onClose={handleCloseErrorReportModal}
        t={t.errorModal}
      />

      {selectedProjectForPartnership && (
        <PartnershipPage 
            project={selectedProjectForPartnership}
            onClose={handleClosePartnershipPage}
            onAddPartnershipLead={handleAddPartnershipLead}
            language={language}
            t={t.partnershipPage}
        />
      )}
      <WhatsAppButton />
    </div>
  );
};

export default App;
